import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FileUploader from "../../../../../../util/component/FileUploader";
import { ContextOperation } from "../../../../context/OperationContext";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import useSnackbar from "../../../../hooks/useSnackbar";
import FormInput from "../../../common/FormInput";
import DamageFormService from "../../service/DamageFormService";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CreatePractice = (props: Props) => {
  const { tenant, setLoading } = useContext(ContextOperation);

  const t = useOperationTranslation();

  const { id } = useParams();
  const snackBar = useSnackbar();

  const [practiceNumber, setPracticeNumber] = useState<string | undefined>();
  const [practiceNumberInvalid, setPracticeNumberInvalid] = useState<boolean>(false);
  const [fileUploaded, setFileUploaded] = useState<File | undefined>();

  const onSave = () => {
    if (!id || !practiceNumber) {
      setPracticeNumberInvalid(true);
      return;
    }
    if (practiceNumberInvalid) return;

    const data = new FormData();
    data.append("file", fileUploaded || "");
    setLoading(true);
    DamageFormService.details.generalInfo.practice.post.create(tenant, id, practiceNumber, data).then(
      () => {
        props.onClose();
        snackBar.show("success", t("damageForm.details.generalInfo.practice.snackbar.create.success"));
        setLoading(false);
      },
      () => {
        snackBar.show("error", t("damageForm.details.generalInfo.practice.snackbar.create.error"));
        setLoading(false);
      }
    )
  }

  const onChangePracticeNumber = (val: string) => {
    if (!val) {
      setPracticeNumberInvalid(true);
    } else {
      setPracticeNumberInvalid(false);
    }

    setPracticeNumber(val);
  }

  useEffect(() => {
    if (!props.open) {
      setPracticeNumberInvalid(false);
      setPracticeNumber(undefined);
    }
  }, [props.open]);

  return (
    <Mui.Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <Mui.DialogTitle>
        <Mui.Grid container alignItems="center">
          <Mui.Grid item xs="auto">
            <Mui.Typography>
              {t("damageForm.details.generalInfo.practice.dialogs.create.title")}
            </Mui.Typography>
          </Mui.Grid>

          <Mui.Grid item xs="auto" ml="auto">
            <Mui.Button variant="text" size="icon" onClick={props.onClose}>
              <MuiIcons.Close color="action" />
            </Mui.Button>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.DialogTitle>

      <Mui.DialogContent>
        <Mui.Stack spacing={2}>
          <FormInput
            name="practiceNumber"
            type="text"
            value={practiceNumber}
            onChange={onChangePracticeNumber}
            label={t("damageForm.details.generalInfo.practice.dialogs.create.lblPracticeNumber")}
            error={practiceNumberInvalid}
            helperText={practiceNumberInvalid && "Practice number is required"}
          />

          <FileUploader onUpload={setFileUploaded} />
        </Mui.Stack>
      </Mui.DialogContent>

      <Mui.DialogActions>
        <Mui.Stack direction="row" spacing={2}>
          <Mui.Button variant="outlined" size="smallPlus" onClick={props.onClose}>
              {t("damageForm.details.generalInfo.practice.dialogs.create.btnCancel")}
          </Mui.Button>

          <Mui.Button variant="contained" size="smallPlus" onClick={onSave}>
              {t("damageForm.details.generalInfo.practice.dialogs.create.btnSave")}
          </Mui.Button>
        </Mui.Stack>
      </Mui.DialogActions>
    </Mui.Dialog>
  )
}

export default CreatePractice;